/**
 * /search Products panel — client-side pagination (Tomas 2026-08-06)
 *
 * Server render uses paginate:16 on search.results which is a mixed
 * pool (products + articles). Products panel filters to products only
 * → typically 14 products per "page of 16", not 16. Misleading count.
 *
 * This module refetches products via `?type=product` (product-only
 * search) so we get exactly 16 products per page. Same client-side
 * pagination pattern as aro-search-page-blogs.js / recipes.js.
 */
(function () {
  'use strict';

  var PAGE_SIZE = 16;
  var terms;
  var state;
  // Stale-request guard: filter changes trigger a fresh init while the
  // previous init's fetches may still be in flight. Without this token
  // the older (stale-filter) fetch resolves AFTER the new render and
  // overwrites the correct results with the previous filter's data.
  var requestId = 0;

  function isOnSearchPage() {
    return /^\/search\/?$/.test(window.location.pathname);
  }

  function getTerms() {
    return new URLSearchParams(location.search).get('q') || '';
  }

  function fetchProductsPage(page) {
    // Preserve all current URL params (filters, sort_by, etc.) — critical
    // for filter-apply re-fetches where the URL has filter.p.m.… params.
    // Only override type + page + section_id.
    var params = new URLSearchParams(location.search);
    params.set('q', terms);
    params.set('type', 'product');
    params.set('page', page);
    params.set('section_id', 'main-search');
    return fetch('/search?' + params.toString()).then(function (r) { return r.text(); }).then(function (html) {
      return new DOMParser().parseFromString(html, 'text/html');
    });
  }

  function extractProductLis(doc) {
    if (!doc) return [];
    var grid = doc.getElementById('product-grid');
    if (!grid) return [];
    return Array.prototype.slice.call(
      grid.querySelectorAll(':scope > li:not(.loading-overlay)')
    );
  }

  function fetchAllProducts() {
    return fetchProductsPage(1).then(function (doc) {
      var all = extractProductLis(doc);
      var pageLinks = doc.querySelectorAll('.pagination a[href*="page="]');
      var maxPage = 1;
      pageLinks.forEach(function (a) {
        var m = a.href.match(/[?&]page=(\d+)/);
        if (m) maxPage = Math.max(maxPage, parseInt(m[1], 10));
      });
      if (maxPage <= 1) return all;
      var promises = [];
      for (var p = 2; p <= maxPage; p++) promises.push(fetchProductsPage(p));
      return Promise.all(promises).then(function (docs) {
        docs.forEach(function (d) { all = all.concat(extractProductLis(d)); });
        return all;
      });
    });
  }

  function renderCurrentPage(page) {
    if (!state || !state.grid || !state.allLis) return;
    state.currentPage = page;
    var start = (page - 1) * PAGE_SIZE;
    var slice = state.allLis.slice(start, start + PAGE_SIZE);
    // Preserve the loading-overlay li at end of grid.
    var overlay = state.grid.querySelector('li.loading-overlay');
    state.grid.innerHTML = '';
    slice.forEach(function (li) { state.grid.appendChild(li.cloneNode(true)); });
    if (overlay) state.grid.appendChild(overlay);
    buildPagination();
    updateCount();
  }

  function updateCount() {
    if (!state) return;
    // Above-grid count line — uniform with Blogs/Recipes ("N Blogs for 'X'").
    var aboveGrid = state.panel.querySelector('.aro-search-page__products-count');
    if (aboveGrid) {
      aboveGrid.textContent = state.totalItems + ' Product' +
        (state.totalItems === 1 ? '' : 's') + ' for ‘' + terms + '’';
    }
  }

  function buildPagination() {
    if (!state) return;
    // Replace the server-rendered pagination-wrapper with fresh theme-style
    // structure using our client-side page counts. Uniform with Blogs/Recipes.
    var wrapper = state.panel.querySelector('.pagination--wrapper');
    if (!wrapper) return;
    var totalPages = Math.ceil(state.totalItems / PAGE_SIZE);
    if (totalPages <= 1) { wrapper.innerHTML = ''; return; }
    wrapper.outerHTML = renderThemePagination(state.currentPage, totalPages, state.totalItems, 'product');
    bindPaginationClicks();
  }

  function bindPaginationClicks() {
    if (!state) return;
    var wrapper = state.panel.querySelector('.pagination--wrapper');
    if (!wrapper) return;
    wrapper.querySelectorAll('a[data-page]').forEach(function (a) {
      a.addEventListener('click', function (e) {
        e.preventDefault();
        if (a.parentElement.classList.contains('disabled')) return;
        var page = parseInt(a.getAttribute('data-page'), 10);
        if (isNaN(page) || page < 1 || page > Math.ceil(state.totalItems / PAGE_SIZE)) return;
        renderCurrentPage(page);
        state.panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  function renderThemePagination(current, totalPages, totalItems, singular) {
    var start = (current - 1) * PAGE_SIZE + 1;
    var end = Math.min(current * PAGE_SIZE, totalItems);
    var progress = totalItems > 0 ? end / totalItems : 0;
    var plural = totalItems === 1 ? singular : singular + 's';
    var prevArrow = '<svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>';
    var nextArrow = '<svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>';
    var pageItems = '';
    for (var p = 1; p <= totalPages; p++) {
      if (p === current) {
        pageItems += '<li class="current"><span class="page">' + p + '</span></li>';
      } else {
        pageItems += '<li class="page"><a href="#" data-page="' + p + '">' + p + '</a></li>';
      }
    }
    return '<div class="pagination--wrapper">' +
      '<div class="thb-filter-count">' +
        '<span class="facets__label">' + start + '-' + end + ' of ' + totalItems + ' ' + plural + '</span>' +
        '<div class="pagination--progress" style="--progress-count: ' + progress + ';"><div class="pagination--progress-bar"></div></div>' +
      '</div>' +
      '<nav class="pagination pagination-type--paginated">' +
        '<div class="row"><div class="small-12 columns">' +
          '<ul class="page-numbers nav-links">' +
            '<li class="prev' + (current === 1 ? ' disabled' : '') + '"><a href="#" data-page="' + (current - 1) + '" aria-label="Previous">' + prevArrow + '</a></li>' +
            pageItems +
            '<li class="next' + (current === totalPages ? ' disabled' : '') + '"><a href="#" data-page="' + (current + 1) + '" aria-label="Next">' + nextArrow + '</a></li>' +
          '</ul>' +
        '</div></div>' +
      '</nav>' +
    '</div>';
  }

  function init() {
    if (!isOnSearchPage()) return;
    var panel = document.querySelector('.aro-search-page__panel--products');
    var grid = document.getElementById('product-grid');
    if (!panel || !grid) return;
    terms = getTerms();
    if (!terms) return;

    var myId = ++requestId;
    state = { panel: panel, grid: grid, allLis: [], totalItems: 0, currentPage: 1, id: myId };

    fetchAllProducts().then(function (allLis) {
      if (state.id !== myId) return; // superseded by newer init
      // Notify sibling panels (blogs/recipes) that the active tab has
      // loaded so they can fire their own fetches. Guarded once-flag —
      // only dispatched on the first successful init, subsequent
      // hookRerender-triggered inits skip.
      _dispatchActiveTabDoneOnce();
      // Client-side Availability filter — Shopify's /search endpoint
      // silently ignores filter.p.tag=_fully-oos, so we filter the fetched
      // product cards locally by the .badge.out-of-stock marker (which the
      // theme renders when product.available === false = every variant
      // sold out AND inventory tracked). Ebooks (tracking off) never get
      // the badge, so they're correctly excluded from OOS results.
      // Collection pages don't need this — Shopify honors the tag filter
      // there server-side.
      var params = new URLSearchParams(location.search);
      if (params.getAll('filter.p.tag').indexOf('_fully-oos') !== -1) {
        allLis = allLis.filter(function (li) { return li.querySelector('.badge.out-of-stock'); });
      }
      state.allLis = allLis;
      state.totalItems = allLis.length;
      // Always render — even when 0. Without this, the server-rendered
      // grid persists when our client-side filter narrows results to
      // nothing (e.g. OOS filter + Publications on /search: server shows
      // the 2 Publications ebooks, our filter removes both, but if we
      // skip renderCurrentPage the server cards stay visible).
      renderCurrentPage(1);
      if (allLis.length === 0) {
        // Show an "empty" state — clear grid, blank pagination, count = 0.
        var overlay = state.grid && state.grid.querySelector('li.loading-overlay');
        if (state.grid) state.grid.innerHTML = '';
        if (overlay && state.grid) state.grid.appendChild(overlay);
        var wrapper = state.panel.querySelector('.pagination--wrapper');
        if (wrapper) wrapper.innerHTML = '';
      }
      updateCount();
    }).catch(function () { /* silent — server render stays */ });
  }

  function hookRerender() {
    if (!window.FacetFiltersForm) return false;
    if (FacetFiltersForm.__aroProductsHooked) return true;
    FacetFiltersForm.__aroProductsHooked = true;
    var orig = FacetFiltersForm.renderProductGridContainer;
    if (typeof orig !== 'function') return true;
    FacetFiltersForm.renderProductGridContainer = function (html) {
      orig.call(this, html);
      // Defer init() to next tick so it runs AFTER FacetFiltersForm's
      // synchronous updateURLHash (called after the sections.forEach in
      // renderPage). Without this, cached-response paths (renderSection-
      // FromCache) fire renderProductGridContainer SYNC while location.
      // search is still the previous URL — our fetch then reads stale
      // filter params and renders the wrong grid.
      setTimeout(function () { try { init(); } catch (e) { /* silent */ } }, 0);
    };
    return true;
  }

  // Priority tab-load: only the active tab (sessionStorage
  // aro-search-page-tab, default 'products') fetches on DOMContentLoaded.
  // The other two panels wait for aro:search-active-tab-done before
  // firing their init. hookRerender always runs so facet re-renders
  // after user interaction fire regardless.
  var _MY_TAB_NAME = 'products';
  var _activeTabDispatchedOnce = false;
  function _isActiveTab() {
    var stored = null;
    try { stored = sessionStorage.getItem('aro-search-page-tab'); } catch (e) {}
    return (stored || 'products') === _MY_TAB_NAME;
  }
  function _dispatchActiveTabDoneOnce() {
    if (_activeTabDispatchedOnce) return;
    _activeTabDispatchedOnce = true;
    try { window.dispatchEvent(new Event('aro:search-active-tab-done')); } catch (e) {}
  }

  function bootstrap() {
    if (!hookRerender()) {
      var tries = 0;
      var iv = setInterval(function () {
        tries++;
        if (hookRerender() || tries > 40) clearInterval(iv);
      }, 100);
    }
    if (_isActiveTab()) {
      init();
    } else {
      window.addEventListener('aro:search-active-tab-done', function () {
        init();
      }, { once: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
